import React, { useMemo } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import type { Order } from '../../services/AlpacaService';
import styles from './PnlChart.module.css';

/** Pair up filled buys and sells per symbol (FIFO) to produce a list of
 * closed round-trip trades with {closedAt, pnl, symbol, qty}. Same logic
 * the Trades page uses for symbol performance — we just keep timestamps. */
interface ClosedTrade {
  symbol: string;
  closedAt: string; // ISO
  pnl: number;
  qty: number;
}

function computeClosedTrades(orders: Order[]): ClosedTrade[] {
  const filled = orders.filter((o) => o.status === 'filled' && o.filled_avg_price);
  const bySymbol = new Map<string, Order[]>();
  for (const o of filled) {
    if (!bySymbol.has(o.symbol)) bySymbol.set(o.symbol, []);
    bySymbol.get(o.symbol)!.push(o);
  }
  const closed: ClosedTrade[] = [];
  Array.from(bySymbol.entries()).forEach(([symbol, list]) => {
    const sorted = [...list].sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime());
    const longs = sorted.filter((o) => o.side === 'buy');
    const shorts = sorted.filter((o) => o.side === 'sell');
    const pairs = Math.min(longs.length, shorts.length);
    for (let i = 0; i < pairs; i++) {
      const buy = longs[i];
      const sell = shorts[i];
      const buyPrice = parseFloat(buy.filled_avg_price || '0');
      const sellPrice = parseFloat(sell.filled_avg_price || '0');
      const qty = Math.min(parseFloat(buy.filled_qty), parseFloat(sell.filled_qty));
      if (!(qty > 0) || !(buyPrice > 0) || !(sellPrice > 0)) continue;
      const pnl = (sellPrice - buyPrice) * qty;
      // A round-trip closes at the later of the two timestamps.
      const closedAt =
        new Date(buy.submitted_at).getTime() > new Date(sell.submitted_at).getTime()
          ? buy.submitted_at
          : sell.submitted_at;
      closed.push({ symbol, closedAt, pnl, qty });
    }
  });
  return closed.sort((a, b) => new Date(a.closedAt).getTime() - new Date(b.closedAt).getTime());
}

interface ChartPoint {
  t: number; // ms since epoch
  dateLabel: string;
  cumulative: number;
  drawdown: number;
  tradePnl: number;
  symbol: string;
}

function buildSeries(trades: ClosedTrade[]): ChartPoint[] {
  let running = 0;
  let hwm = 0;
  return trades.map((t) => {
    running += t.pnl;
    if (running > hwm) hwm = running;
    const drawdown = running - hwm; // always <= 0
    const d = new Date(t.closedAt);
    return {
      t: d.getTime(),
      dateLabel: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      cumulative: Number(running.toFixed(2)),
      drawdown: Number(drawdown.toFixed(2)),
      tradePnl: Number(t.pnl.toFixed(2)),
      symbol: t.symbol,
    };
  });
}

const fmtMoney = (n: number) => {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(2)}`;
};

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload as ChartPoint;
  if (!d) return null;
  const cumColor = d.cumulative >= 0 ? 'var(--color-positive)' : 'var(--color-negative)';
  const tradeColor = d.tradePnl >= 0 ? 'var(--color-positive)' : 'var(--color-negative)';
  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipDate}>{d.dateLabel}</div>
      <div>
        <span className={styles.tooltipLabel}>Trade ({d.symbol}):</span>{' '}
        <span style={{ color: tradeColor, fontWeight: 700 }}>
          {d.tradePnl >= 0 ? '+' : ''}
          {fmtMoney(d.tradePnl)}
        </span>
      </div>
      <div>
        <span className={styles.tooltipLabel}>Cumulative P&amp;L:</span>{' '}
        <span style={{ color: cumColor, fontWeight: 700 }}>
          {d.cumulative >= 0 ? '+' : ''}
          {fmtMoney(d.cumulative)}
        </span>
      </div>
      {d.drawdown < 0 && (
        <div>
          <span className={styles.tooltipLabel}>Drawdown:</span>{' '}
          <span style={{ color: 'var(--color-negative)' }}>{fmtMoney(d.drawdown)}</span>
        </div>
      )}
    </div>
  );
};

export interface PnlChartProps {
  orders: Order[];
}

const PnlChart: React.FC<PnlChartProps> = ({ orders }) => {
  const { series, kpis } = useMemo(() => {
    const trades = computeClosedTrades(orders);
    const s = buildSeries(trades);
    if (s.length === 0) {
      return { series: s, kpis: null };
    }
    const totalPnl = s[s.length - 1].cumulative;
    const wins = trades.filter((t) => t.pnl > 0).length;
    const losses = trades.filter((t) => t.pnl < 0).length;
    const winRate = wins + losses > 0 ? (wins / (wins + losses)) * 100 : null;
    const maxDrawdown = Math.min(0, ...s.map((p) => p.drawdown));
    const bestTrade = Math.max(0, ...trades.map((t) => t.pnl));
    const worstTrade = Math.min(0, ...trades.map((t) => t.pnl));

    // Return % = total P&L / total capital deployed (sum of buy costs).
    // This is "return on capital deployed" — the most honest % when we
    // don't know the full account equity at period start.
    const totalDeployed = trades.reduce((sum, t) => {
      // Find the matching buy order to get cost basis
      const buyOrders = orders.filter(
        (o) => o.symbol === t.symbol && o.side === 'buy' && o.status === 'filled' && o.filled_avg_price,
      );
      if (buyOrders.length > 0) {
        return sum + parseFloat(buyOrders[0].filled_avg_price || '0') * t.qty;
      }
      return sum;
    }, 0);
    const returnPct = totalDeployed > 0 ? (totalPnl / totalDeployed) * 100 : null;

    // Best / worst DAY aggregates.
    const byDay = new Map<string, number>();
    for (const t of trades) {
      const key = new Date(t.closedAt).toISOString().slice(0, 10);
      byDay.set(key, (byDay.get(key) || 0) + t.pnl);
    }
    const dayPnls = Array.from(byDay.values());
    const bestDay = dayPnls.length ? Math.max(...dayPnls) : 0;
    const worstDay = dayPnls.length ? Math.min(...dayPnls) : 0;

    return {
      series: s,
      kpis: {
        totalPnl,
        returnPct,
        trades: trades.length,
        winRate,
        maxDrawdown,
        bestTrade,
        worstTrade,
        bestDay,
        worstDay,
      },
    };
  }, [orders]);

  if (series.length === 0) {
    return (
      <div className={styles.empty}>
        No closed round-trip trades in this window yet. Once you have at least one buy + sell on the same symbol, the
        chart populates.
      </div>
    );
  }

  const totalColor = kpis && kpis.totalPnl >= 0 ? 'var(--color-positive, #2ecc71)' : 'var(--color-negative, #e74c3c)';

  return (
    <div className={styles.wrap}>
      {kpis && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}>
            <div className={styles.kpiLabel}>Total P&amp;L</div>
            <div className={styles.kpiValue} style={{ color: totalColor }}>
              {kpis.totalPnl >= 0 ? '+' : ''}
              {fmtMoney(kpis.totalPnl)}
            </div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiLabel}>Return %</div>
            <div className={styles.kpiValue} style={{ color: totalColor }}>
              {kpis.returnPct != null ? `${kpis.returnPct >= 0 ? '+' : ''}${kpis.returnPct.toFixed(1)}%` : '—'}
            </div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiLabel}>Trades</div>
            <div className={styles.kpiValue}>{kpis.trades}</div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiLabel}>Win Rate</div>
            <div className={styles.kpiValue}>{kpis.winRate != null ? `${kpis.winRate.toFixed(0)}%` : '—'}</div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiLabel}>Max Drawdown</div>
            <div className={styles.kpiValue} style={{ color: 'var(--color-negative, #e74c3c)' }}>
              {fmtMoney(kpis.maxDrawdown)}
            </div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiLabel}>Best Day</div>
            <div className={styles.kpiValue} style={{ color: 'var(--color-positive, #2ecc71)' }}>
              +{fmtMoney(kpis.bestDay)}
            </div>
          </div>
          <div className={styles.kpi}>
            <div className={styles.kpiLabel}>Worst Day</div>
            <div className={styles.kpiValue} style={{ color: 'var(--color-negative, #e74c3c)' }}>
              {fmtMoney(kpis.worstDay)}
            </div>
          </div>
        </div>
      )}
      <div className={styles.chartWrap}>
        {kpis && kpis.returnPct != null && (
          <div className={styles.returnBadge} style={{ color: totalColor }}>
            {kpis.returnPct >= 0 ? '+' : ''}
            {kpis.returnPct.toFixed(1)}%
          </div>
        )}
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={series} margin={{ top: 10, right: 16, bottom: 10, left: 16 }}>
            <defs>
              <linearGradient id="pnlFillPos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2ecc71" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#2ecc71" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="pnlFillNeg" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#e74c3c" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#e74c3c" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              dataKey="dateLabel"
              tick={{ fill: 'var(--text-secondary)', fontSize: 11 }}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis tickFormatter={fmtMoney} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} width={70} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />
            <Tooltip content={<CustomTooltip />} />
            {/* Two Areas so the shading flips color at zero. One clips positive,
                one clips negative. */}
            <Area
              type="monotone"
              dataKey={(d: ChartPoint) => (d.cumulative >= 0 ? d.cumulative : 0)}
              stroke="transparent"
              fill="url(#pnlFillPos)"
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey={(d: ChartPoint) => (d.cumulative < 0 ? d.cumulative : 0)}
              stroke="transparent"
              fill="url(#pnlFillNeg)"
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="cumulative"
              stroke="#3498db"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PnlChart;
